import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ROLE_LEVEL as ROLE_RANK } from '@/lib/role';

/**
 * ProtectedRoute - Client-side route protection
 *
 * SECURITY NOTE (A14): This component provides UX protection only.
 * Real security is enforced by the backend on every API call:
 * - Backend validates JWT token on every request
 * - Backend verifies user roles from database, not from client
 * - Even if sessionStorage is manipulated, API calls will fail with 401/403
 *
 * C8 refactor: Eliminada la lógica de redirect a /tenant-selection.
 * La selección de client se maneja por ClientContext + FirstUseClientPicker.
 * ProtectedRoute solo valida que haya JWT válido; si no → /login.
 *
 * A01 defense-in-depth (2026-06-09): la prop `adminOnly` añade un guard de ROL
 * en cliente para rutas admin (acceso por URL directa aunque el nav las oculte).
 * NO es la línea de seguridad real — el backend exige require_admin en cada
 * endpoint y devuelve 403. Esto es solo UX para no renderizar páginas vacías.
 *
 * QA backlog #6 (2026-06-20): añadida la prop `minRole` con jerarquía
 * user < viewer < analyst < admin para guardar las rutas SOC (alerts,
 * attack-library, sigma/yara, vulnerabilities, compliance, reports, ad-attacks,
 * correlation...). El backend ya devuelve 403 (RBAC LIVE); esto es solo UX para
 * que el rol `user` no acceda por URL directa a páginas que se le ocultan en el
 * menú. NULL-safe: si no se puede decodificar el rol, se deja pasar (el backend
 * sigue enforce-ando).
 *
 * Además fuerza el cambio de contraseña (#4): si `mustChangePassword` está en
 * sessionStorage, bloquea toda la app y redirige a /change-password.
 */

// La jerarquía de roles se importa arriba de `lib/role.js`: UNA sola tabla,
// alineada con `ROLE_LEVEL` del backend (auth/dependencies.py:351).
// Antes había aquí una copia a la que le faltaba `ejecutor`, así que ese rol
// caía en `ROLE_RANK[role] === undefined` y la guarda `minRole` no se le
// aplicaba (se dejaba pasar). No era un agujero —el backend devuelve 403—,
// pero sí una tabla de roles que no cuadraba con la del servidor.

const decodeRoleFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role ?? null;
  } catch (e) {
    return null;
  }
};

const ProtectedRoute = ({ children, adminOnly = false, minRole = null }) => {
  const [authState, setAuthState] = useState(null); // null=loading

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const hasValidToken = !!(token && token.split('.').length === 3);
    const role = hasValidToken ? decodeRoleFromToken(token) : null;
    setAuthState({ authenticated: hasValidToken, role });
  }, []);

  // Loading state
  if (authState === null) {
    return (
      <div className="min-h-screen bg-v4-bg flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent"></div>
          <p className="mt-4 text-slate-400">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → login
  if (!authState.authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Cambio de contraseña forzado (#4): mientras el flag siga activo, ninguna
  // ruta protegida es accesible salvo la propia /change-password.
  if (
    sessionStorage.getItem('mustChangePassword') === 'true' &&
    window.location.pathname !== '/change-password'
  ) {
    return <Navigate to="/change-password" replace />;
  }

  // Admin-only route: if we could decode a role and it is not admin/superadmin,
  // redirect home. NULL-safe: if role is unknown (couldn't decode), let through
  // — the backend will still enforce 403.
  if (adminOnly && authState.role && !['admin', 'superadmin'].includes(authState.role)) {
    return <Navigate to="/" replace />;
  }

  // minRole guard (#6): rutas SOC requieren al menos cierto rol en la jerarquía
  // user < viewer < analyst < admin. NULL-safe: si el rol no se pudo decodificar
  // (o es desconocido), se deja pasar — el backend sigue devolviendo 403.
  if (minRole && authState.role && ROLE_RANK[authState.role]) {
    const required = ROLE_RANK[minRole] || 0;
    if (ROLE_RANK[authState.role] < required) {
      return <Navigate to="/" replace />;
    }
  }

  // All checks passed — client selection handled by ClientContext
  return children;
};

export default ProtectedRoute;
